import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';
import { Button } from '@/components/ui/button';
import { Heart, ArrowRight } from 'lucide-react';

const faq = [
  { q: 'How do I write a heartfelt message to my mom?', a: 'Start with a memory, express how you feel, and keep it honest. It doesn\'t need to be perfect — just real.' },
  { q: 'What if I don\'t know what to say?', a: 'Use one of the examples above, or start simple. Even a few honest words mean everything.' },
  { q: 'Is it better to write or say it?', a: 'Both matter — but writing creates something lasting she can revisit anytime.' },
  { q: 'Can I send this privately?', a: 'Yes. Your message can be private or shared with only the person you choose.' },
];

const related = [
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
  { label: 'Thank You Message for Parents', href: '/thank-you-message-for-parents' },
  { label: 'Message to Someone You Love', href: '/message-to-someone-you-love' },
  { label: 'Living Tribute Ideas', href: '/living-tribute-ideas' },
  { label: 'Create a Message', href: '/signup' },
];

function InlineCTA({ label }: { label: string }) {
  return (
    <div className="not-prose my-8 text-center">
      <Button asChild size="lg" className="gap-2">
        <Link to="/signup"><Heart className="w-4 h-4" /> {label}</Link>
      </Button>
    </div>
  );
}

export default function HeartfeltMessageToMom() {
  return (
    <SEOPageLayout
      title="Heartfelt Message to Mom — Words She Deserves to Hear"
      metaDescription="Find the right words to tell your mom how much she means to you. Real examples, emotional guidance, and a free message creator to say it now — not later."
      h1="Heartfelt Message to Mom — Words She Deserves to Hear"
      subtitle="Your mom gave you everything — her time, her love, her strength. But the truth is, most of us don't always say what we feel while we still can."
      faq={faq}
      relatedPages={related}
    >
      <p>
        A heartfelt message to your mom isn't just a kind gesture — it's something she carries with her forever.
      </p>
      <p>Whether you're looking for:</p>
      <ul>
        <li>a <strong>message for your mom</strong></li>
        <li>a <strong>thank you message for mom</strong></li>
        <li>or emotional <strong>words to say to your mother</strong></li>
      </ul>
      <p>This guide will help you find the right words — and actually say them.</p>

      <h2>Why Writing a Heartfelt Message to Your Mom Matters</h2>
      <p>Most people assume there will always be more time.</p>
      <p>More time to say thank you. More time to express love. More time to say what really matters.</p>
      <p>But life doesn't always give us that chance.</p>
      <p>A heartfelt message to your mom:</p>
      <ul>
        <li><strong>Strengthens your relationship</strong></li>
        <li><strong>Gives her something she'll never forget</strong></li>
        <li><strong>Creates a moment that lasts</strong> far beyond the day</li>
      </ul>
      <p>
        And yet, so many people never send one — not because they don't care, but because they don't know what to say.
      </p>

      <InlineCTA label="Create Your Message Now" />

      <h2>Heartfelt Message to Mom Examples</h2>
      <p>Here are real examples you can use or personalize:</p>

      <blockquote>
        "Mom, I know I don't say this enough, but everything I am is because of you. Thank you for loving me even when I didn't make it easy."
      </blockquote>

      <blockquote>
        "You've always been my safe place. Even when life felt heavy, knowing you were there made everything feel lighter."
      </blockquote>

      <blockquote>
        "Thank you for the sacrifices I didn't see at the time. I see them now — and I'll never forget them."
      </blockquote>

      <blockquote>
        "You taught me what love looks like without ever needing to explain it. I carry that with me every day."
      </blockquote>

      <h2>Short Messages You Can Copy and Use</h2>
      <p>If you don't know what to write, start here:</p>
      <ul>
        <li>"I love you more than I say, but I'm saying it now."</li>
        <li>"Thank you for always being there, even when I didn't notice."</li>
        <li>"You are the reason I believe in love and strength."</li>
        <li>"I appreciate you more than words can explain."</li>
        <li>"Everything good in me started with you."</li>
      </ul>

      <InlineCTA label="Write My Message" />

      <h2>How to Write a Message to Your Mom (Simple Framework)</h2>
      <p>If you want to write your own message, follow this:</p>
      <ol>
        <li>
          <strong>Start with a real memory.</strong> Think of a moment that meant something to you.<br />
          <em>"I still remember when you stayed up all night helping me…"</em>
        </li>
        <li>
          <strong>Say what she means to you.</strong> Be honest, not perfect.<br />
          <em>"You've always been my strength, even when I didn't realize it."</em>
        </li>
        <li>
          <strong>Express gratitude or love.</strong><br />
          <em>"Thank you for everything you've done for me."</em>
        </li>
        <li>
          <strong>Close with something direct.</strong><br />
          <em>"I love you more than I've ever said."</em>
        </li>
      </ol>

      <h2>When Should You Send a Message to Your Mom?</h2>
      <p>Most people wait for Mother's Day, birthdays, or special occasions.</p>
      <p>But the most powerful messages come when they're <strong>unexpected</strong>.</p>
      <p>A random Tuesday means more than a scheduled holiday. That's when your words feel real.</p>

      <h2>Appreciation Message to Mom (Deeper Examples)</h2>
      <p>If you want something more emotional:</p>
      <blockquote>
        "Mom, I've been thinking about everything you've done for me — the things I saw, and the things I didn't. I realize now how much you gave up so I could have more. I just want you to know… it mattered. You matter. And I love you deeply."
      </blockquote>

      <InlineCTA label="Create Your Free Message" />

      <h2>Turn Your Words Into Something She Can Keep Forever</h2>
      <p>
        With <Link to="/signup">Say It NOW</Link>, your message becomes more than text. It becomes:
      </p>
      <ul>
        <li>A <strong>beautifully designed digital tribute</strong></li>
        <li>A <strong>private or shareable</strong> experience</li>
        <li>Something she can <strong>return to anytime</strong></li>
      </ul>
      <p>This isn't just a message. It's something she'll remember.</p>
    </SEOPageLayout>
  );
}
